import { isStartBlock } from '@/util/blocks';
import List from '@/components/common/List';
import { useApplication } from '@/hooks/useApplication';
import { useDialogs } from '@/hooks/useDialogs';
import { useMetadata } from '@/hooks/useMetadata';
import { useUpdateNodes } from '@/hooks/useUpdateNodes';
import { Detail } from '@/types/detail';
import { SearchBlocksItem } from '@/types/search';
import { Subroutine } from '@/types/subroutine';
import { Variable, VariableScope } from '@/types/variable';
import { isObject, isString } from '@/util/typeGuards';
import { variableIsInUse } from '@/util/variables';
import { Button } from '@mui/material';
import { ConfirmOptions } from 'material-ui-confirm';
import { FC, useRef, useState } from 'react';
import VariableListItem from './VariableListItem';
import VariableListItemEdit from './VariableListItemEdit';

type VariableManagerProps = {
  scope: VariableScope;
  currentSubroutine?: Subroutine;
  query: string;
  variables: Variable[];
  filteredVars: Variable[];
  onVariableSearch: (searchValue: SearchBlocksItem) => void;
  removeSearchFocus: () => void;
};

const VariableManager: FC<VariableManagerProps> = (props) => {
  const { application, setApplication } = useApplication();
  const { setUpdateNodes } = useUpdateNodes();
  const { metadata } = useMetadata();
  const { confirm } = useDialogs();
  const addNewRef = useRef<HTMLButtonElement>(null);

  const [openEdit, setOpenEdit] = useState(false);

  const confirmExists: ConfirmOptions = {
    title: 'Variable already exists',
    description: `A variable with this name already exists within this scope ${
      props.currentSubroutine?.id && 'and subroutine'
    }`,
    confirmationText: 'Ok',
    hideCancelButton: true
  };

  const handleUpdateNodes = () => {
    setUpdateNodes(
      props.scope === 'local' ? props.currentSubroutine?.nodes?.map((node) => node.toString()) || [] : ['*']
    );
  };

  const handleOnSave = (newName: string, type: string, oldName?: string) => {
    let updatedVariables = structuredClone(props.variables);
    let details = application.blocks.details;

    if (!oldName) {
      updatedVariables[updatedVariables.length] = { name: newName, type: type };
    } else {
      updatedVariables = updatedVariables.map((variable) => {
        if (variable.name === oldName) {
          return { name: newName, type: type };
        } else {
          return variable;
        }
      });

      if (props.scope !== 'local') {
        const stringifiedDetails = JSON.stringify(details);
        details = JSON.parse(
          stringifiedDetails.replaceAll(
            `"name":"${oldName}","scope":"${props.scope}"`,
            `"name":"${newName}","scope":"${props.scope}"`
          )
        );
        application.promptGroups = application.promptGroups.map((group) => {
          if (isObject(group?.path) && group.path.name === oldName && group.path.scope === 'session') {
            return { ...group, path: { name: newName, scope: group.path.scope } };
          }
          return group;
        });
      } else {
        const nodes: Record<string, Detail> = {};
        props.currentSubroutine?.nodes.forEach((node) => {
          if (!props.currentSubroutine) return;
          const applicableNode = application.blocks.details[node];
          if (isStartBlock(application.blocks.nodes[node])) {
            // Only the bindings follow a rename - the interface itself does not change.
            props.currentSubroutine.arg_bindings = props.currentSubroutine.arg_bindings.map((binding) =>
              binding === oldName ? newName : binding
            );
            props.currentSubroutine.event_arg_bindings = props.currentSubroutine.event_arg_bindings?.map((binding) =>
              binding === oldName ? newName : binding
            );
          }

          nodes[node] = JSON.parse(
            JSON.stringify(applicableNode).replaceAll(
              `"name":"${oldName}","scope":"local"`,
              `"name":"${newName}","scope":"local"`
            )
          );
        });

        details = { ...details, ...nodes };
      }
    }

    if (updatedVariables.filter((variable) => variable.name === newName).length > 1) {
      confirm(confirmExists);
      return;
    }
    setApplication({
      ...application,
      variables: convertVariables(updatedVariables),
      blocks: { ...application.blocks, details: details }
    });
    handleUpdateNodes();
    props.removeSearchFocus();
  };

  const convertVariables = (updatedVariables: Variable[]) =>
    props.currentSubroutine?.id && props.scope === 'local'
      ? {
          ...application.variables,
          local: {
            ...application.variables.local,
            [props.currentSubroutine.id]: updatedVariables
          }
        }
      : {
          ...application.variables,
          [props.scope]: updatedVariables
        };

  return (
    <List>
      {metadata.variables &&
        props.scope !== 'local' &&
        metadata.variables[props.scope]?.map((variable, index) => (
          <VariableListItem
            application={application}
            key={`metavars.items.${index}`}
            readOnly
            variableIsUsed={variableIsInUse(variable, props.scope, application, props.currentSubroutine)}
            variableName={variable.name}
            variableType={variable.type}
            scope={props.scope}
            onVariableSearch={props.onVariableSearch}
          />
        ))}
      {props.filteredVars?.map((filteredVar, index) => {
        return filteredVar.name.startsWith('_debug') ? (
          <span key={`vars.items.${index}`}></span>
        ) : (
          <VariableListItem
            application={application}
            key={`vars.items.${index}`}
            variableIsUsed={variableIsInUse(filteredVar, props.scope, application, props.currentSubroutine)}
            variableName={filteredVar.name}
            variableType={filteredVar.type}
            scope={props.scope}
            onVariableSearch={props.onVariableSearch}
            onSave={(name, type) => handleOnSave(name, type, filteredVar.name)}
            onDelete={() => {
              if (!isString(props.variables)) {
                setApplication({
                  ...application,
                  variables: convertVariables(props.variables.filter((tempVar) => tempVar.name !== filteredVar.name))
                });
                handleUpdateNodes();
              }
            }}
          />
        );
      })}
      {openEdit && (
        <VariableListItemEdit
          scope={props.scope}
          onClose={() => setOpenEdit(false)}
          onSave={(variableName: string, variableType: string) => {
            handleOnSave(variableName, variableType);
            addNewRef.current?.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
            addNewRef.current?.focus();
          }}
        />
      )}
      <Button
        ref={addNewRef}
        variant='contained'
        color='primary'
        disableFocusRipple
        sx={{
          ml: 2,
          mt: 2,
          '&:focus': {
            opacity: '60%'
          }
        }}
        onClick={() => {
          addNewRef.current?.blur();
          setOpenEdit(true);
        }}
      >
        Add new
      </Button>
    </List>
  );
};

export default VariableManager;
